import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { API_ENDPOINTS, API_BASE_URL } from '../../api/config';
import { FaChevronDown, FaClipboardList, FaBolt, FaCheckCircle, FaLock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import ElevatedButton from '../Layout/ElevatedButton';

const TicketCard = styled.div`
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.backgroundSecondary};
  margin-bottom: 1rem;
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
    background: ${props => props.theme.background};
  }
`;

const TicketHeader = styled.div`
  padding: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${props => props.theme.cardBackground};
  }
`;

const TicketLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const TicketIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'open': return props.theme.buttonPrimary;
        case 'in-progress': return props.theme.warning; // Changed to hyphenated
      case 'resolved': return props.theme.success;
      case 'closed': return props.theme.textMuted;
      default: return props.theme.buttonPrimary;
    }
  }};
  color: ${props => props.theme.text};
  font-size: 1.2rem;
`;

const TicketContent = styled.div`
  flex: 1;
`;

const TicketTitle = styled.h4`
  color: ${props => props.theme.text};
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
`;

const TicketMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: ${props => props.theme.textMuted};
`;

const TicketRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PriorityBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;

  background: ${props => {
    switch (props.priority) {
        case 'critical': return '#dc3545'; // Changed to match backend value
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  }};
  color: ${props => props.theme.text};
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;

  background: ${props => {
    switch (props.status) {
      case 'open': return '#5865f2';
      case 'in-progress': return '#ffa500';
      case 'resolved': return '#28a745';
      case 'closed': return '#6c757d';
      default: return '#5865f2';
    }
  }};

  color: white;
`;

const ExpandIcon = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 1rem;
  transition: transform 0.2s ease;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const TicketDetails = styled.div`
  border-top: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.cardBackground};
  max-height: ${props => props.expanded ? '800px' : '0'};
  overflow-y: ${props => props.expanded ? 'auto' : 'hidden'};
  transition: max-height 0.3s ease;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.backgroundSecondary};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #5865f2;
  }
`;

const TicketBody = styled.div`
  padding: 1rem;
`;

const TicketDescription = styled.div`
  color: ${props => props.theme.text};
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const TicketInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoLabel = styled.span`
  color: ${props => props.theme.textMuted};
`;

const InfoValue = styled.span`
  color: ${props => props.theme.text};
  font-weight: 500;
`;

const CommentsSection = styled.div`
  border-top: 1px solid ${props => props.theme.border};
  padding-top: 1rem;
`;

const Comment = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #2f3136;
  border-radius: 6px;
  border-left: 3px solid ${props => props.isAdmin ? '#5865f2' : '#28a745'};
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const CommentAuthor = styled.span`
  font-weight: 600;
  color: ${props => props.isAdmin ? '#5865f2' : '#28a745'};
`;

const CommentTime = styled.span`
  font-size: 0.8rem;
  color: #a0a0a0;
`;

const CommentContent = styled.div`
  color: #ffffff;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #a0a0a0;
  padding: 2rem;
`;

const LoadingState = styled.div`
  text-align: center;
  color: #a0a0a0;
  padding: 2rem;
`;

const ReplyForm = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: ${props => props.theme.cardBackground};
  border-radius: 6px;
  border: 1px solid ${props => props.theme.border};
`;

const ReplyTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  min-height: 80px;
  resize: vertical;
  margin-bottom: 0.75rem;

  &:focus {
    outline: none;
    border-color: #5865f2;
  }
`;

const ReplyActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const StatusSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #5865f2;
  }
`;

const StatusActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme.border};
`;

const SupportTickets = ({ isAdmin = false }) => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedTickets, setExpandedTickets] = useState(new Set());
  const [replyText, setReplyText] = useState({});
  const [replyLoading, setReplyLoading] = useState({});
  const [statusLoading, setStatusLoading] = useState({});
  const [claimLoading, setClaimLoading] = useState({});

  const isStaffUser = user?.role === 'admin' || user?.role === 'support';

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = isAdmin ? API_ENDPOINTS.SUPPORT_ADMIN_TICKETS : API_ENDPOINTS.SUPPORT_TICKETS;
      const response = await fetch(endpoint, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tickets');
      }

      const data = await response.json();
      setTickets(data.tickets || []);
    } catch (error) {
      setError(error.message);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const toggleTicket = (ticketId) => {
    const newExpanded = new Set(expandedTickets);
    if (newExpanded.has(ticketId)) {
      newExpanded.delete(ticketId);
    } else {
      newExpanded.add(ticketId);
    }
    setExpandedTickets(newExpanded);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <FaClipboardList />;
      case 'in-progress': return <FaBolt />;
      case 'resolved': return <FaCheckCircle />;
      case 'closed': return <FaLock />;
      default: return <FaClipboardList />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleReply = async (ticketId, message) => {
    if (!message?.trim()) return;

    const originalMessage = message.trim();

    // Clear reply text keyed by the ticket _id for immediate UX feedback
    setReplyText(prev => ({ ...prev, [ticketId]: '' }));
    setReplyLoading(prev => ({ ...prev, [ticketId]: true }));

    try {
      // Find the numeric ticket id (backend expects numeric `id`)
      const ticketObj = tickets.find(t => t._id === ticketId);
      const numericId = ticketObj?.id;

      const endpoint = isAdmin
        ? `${API_BASE_URL}/support/ticket/${numericId}/response`
        : API_ENDPOINTS.SUPPORT_TICKET_REPLY(numericId);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: originalMessage })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reply');
      }

      // Optimistically update the ticket responses using _id matching
      setTickets(prevTickets => prevTickets.map(ticket => {
        if (ticket._id === ticketId) {
          const newResponse = isAdmin ? {
            adminId: user.id,
            adminName: user.name,
            adminRole: user.role,
            response: originalMessage,
            timestamp: new Date().toISOString(),
            isFromUser: false
          } : {
            userId: user.id,
            userName: user.name,
            response: originalMessage,
            timestamp: new Date().toISOString(),
            isFromUser: true
          };

          return {
            ...ticket,
            responses: [...(ticket.responses || []), newResponse],
            updatedAt: new Date().toISOString(),
            status: isAdmin ? 'waiting-for-user' : 'in-progress'
          };
        }
        return ticket;
      }));

      toast.success('Reply sent successfully');
    } catch (error) {
      console.error('Reply error:', error);
      // Restore the message on error
      setReplyText(prev => ({ ...prev, [ticketId]: originalMessage }));
      toast.error(error.message || 'Failed to send reply');
    } finally {
      setReplyLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  const handleStatusUpdate = async (ticketId, newStatus) => {
    setStatusLoading(prev => ({ ...prev, [ticketId]: true }));

    try {
      const ticketObj = tickets.find(t => t._id === ticketId);
      const numericId = ticketObj?.id;

      const response = await fetch(API_ENDPOINTS.SUPPORT_TICKET_STATUS(numericId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update status');
      }

      // Update ticket status locally using _id
      setTickets(prevTickets => prevTickets.map(ticket => {
        if (ticket._id === ticketId) {
          return { ...ticket, status: newStatus, updatedAt: new Date().toISOString() };
        }
        return ticket;
      }));

      toast.success(`Ticket status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(error.message || 'Failed to update ticket status');
    } finally {
      setStatusLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  const handleClaimTicket = async (ticketId, action) => {
    setClaimLoading(prev => ({ ...prev, [ticketId]: true }));

    try {
      const ticketObj = tickets.find(t => t._id === ticketId);
      const numericId = ticketObj?.id;

      const endpoint = action === 'claim'
        ? API_ENDPOINTS.SUPPORT_TICKET_CLAIM(numericId)
        : API_ENDPOINTS.SUPPORT_TICKET_UNCLAIM(numericId);

      const response = await fetch(endpoint, { method: 'PUT', credentials: 'include' });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to ${action} ticket`);
      }

      // Update ticket assignment locally using _id
      setTickets(prevTickets => prevTickets.map(ticket => {
        if (ticket._id === ticketId) {
          if (action === 'claim') {
            return { ...ticket, assignedTo: user.id, assignedToName: user.name, updatedAt: new Date().toISOString() };
          } else {
            return { ...ticket, assignedTo: null, assignedToName: null, updatedAt: new Date().toISOString() };
          }
        }
        return ticket;
      }));

      toast.success(`Ticket ${action}ed successfully`);
    } catch (error) {
      console.error(`${action} ticket error:`, error);
      toast.error(error.message || `Failed to ${action} ticket`);
    } finally {
      setClaimLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  if (loading) {
    return <LoadingState>Loading support tickets...</LoadingState>;
  }

  if (error) {
    return <EmptyState>Error loading tickets: {error}</EmptyState>;
  }

  if (tickets.length === 0) {
    return (
      <EmptyState>
        {isAdmin ? 'No support tickets found.' : 'You haven\'t created any support tickets yet.'}
      </EmptyState>
    );
  }

  return (
    <div>
      {tickets.map(ticket => (
        <TicketCard key={ticket._id}>
          <TicketHeader onClick={() => toggleTicket(ticket._id)}>
            <TicketLeft>
              <TicketIcon status={ticket.status}>
                {getStatusIcon(ticket.status)}
              </TicketIcon>
              <TicketContent>
                <TicketTitle>{ticket.subject}</TicketTitle>
                <TicketMeta>
                  <span>#{ticket.id}</span>
                  <span>{formatDate(ticket.createdAt)}</span>
                  <span>{ticket.category}</span>
                </TicketMeta>
              </TicketContent>
            </TicketLeft>
            <TicketRight>
              <PriorityBadge priority={ticket.priority}>
                {ticket.priority}
              </PriorityBadge>
              <StatusBadge status={ticket.status}>
                {ticket.status.replace('_', ' ')}
              </StatusBadge>
              <ExpandIcon expanded={expandedTickets.has(ticket._id)}>
                <FaChevronDown />
              </ExpandIcon>
            </TicketRight>
          </TicketHeader>

          <TicketDetails expanded={expandedTickets.has(ticket._id)}>
            <TicketBody>
              <TicketDescription>{ticket.description}</TicketDescription>

              <TicketInfo>
                <InfoItem>
                  <InfoLabel>Status:</InfoLabel>
                  <InfoValue>{ticket.status.replace('_', ' ')}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Priority:</InfoLabel>
                  <InfoValue>{ticket.priority}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Category:</InfoLabel>
                  <InfoValue>{ticket.category}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Created:</InfoLabel>
                  <InfoValue>{formatDate(ticket.createdAt)}</InfoValue>
                </InfoItem>
                {ticket.updatedAt && (
                  <InfoItem>
                    <InfoLabel>Last Updated:</InfoLabel>
                    <InfoValue>{formatDate(ticket.updatedAt)}</InfoValue>
                  </InfoItem>
                )}
              </TicketInfo>

              {ticket.responses && ticket.responses.length > 0 && (
                <CommentsSection>
                  <h4 style={{ color: '#ffffff', marginBottom: '1rem' }}>Responses</h4>
                  {ticket.responses.map((response, index) => (
                    <Comment key={index} isAdmin={!response.isFromUser}>
                      <CommentHeader>
                        <CommentAuthor isAdmin={!response.isFromUser}>
                          {response.isFromUser
                            ? 'Customer'
                            : (response.adminRole === 'admin' ? 'Admin Agent' : 'Support Agent')}
                        </CommentAuthor>
                        <CommentTime>{formatDate(response.timestamp)}</CommentTime>
                      </CommentHeader>
                      <CommentContent>{response.response}</CommentContent>
                    </Comment>
                  ))}
                </CommentsSection>
              )}

              {/* Reply Form */}
              {(!isAdmin || (isAdmin && isStaffUser)) && (
                <ReplyForm>
                  <ReplyTextarea
                    placeholder={isAdmin ? "Add a response to this ticket..." : "Add a reply to this ticket..."}
                    value={replyText[ticket._id] || ''}
                    onChange={(e) => setReplyText(prev => ({ ...prev, [ticket._id]: e.target.value }))}
                    disabled={replyLoading[ticket._id]}
                  />
                  <ReplyActions>
                    <ElevatedButton
                      onClick={() => handleReply(ticket._id, replyText[ticket._id])}
                      disabled={!replyText[ticket._id]?.trim() || replyLoading[ticket._id]}
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    >
                      {replyLoading[ticket._id] ? 'Sending...' : (isAdmin ? 'Send Response' : 'Send Reply')}
                    </ElevatedButton>
                  </ReplyActions>
                </ReplyForm>
              )}

              {/* Staff Status Management */}
              {isAdmin && isStaffUser && (
                <StatusActions>
                  <div style={{ color: '#ffffff', fontSize: '0.875rem', fontWeight: '500' }}>
                    Update Status:
                  </div>
                  <StatusSelect
                    value={ticket.status}
                    onChange={(e) => handleStatusUpdate(ticket._id, e.target.value)}
                    disabled={statusLoading[ticket._id]}
                  >
                    <option value="open">Open</option>
                    <option value="in-progress">In Progress</option>
                    <option value="waiting-for-user">Waiting for User</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </StatusSelect>
                  {statusLoading[ticket._id] && (
                    <span style={{ color: '#a0a0a0', fontSize: '0.875rem' }}>Updating...</span>
                  )}

                  {/* Claim/Unclaim Buttons */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                    {!ticket.assignedTo ? (
                      <ElevatedButton
                          onClick={() => handleClaimTicket(ticket._id, 'claim')}
                          disabled={claimLoading[ticket._id]}
                          style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                        >
                          {claimLoading[ticket._id] ? 'Claiming...' : 'Claim Ticket'}
                        </ElevatedButton>
                    ) : ticket.assignedTo?.toString() === user?.id ? (
                      <ElevatedButton
                        onClick={() => handleClaimTicket(ticket._id, 'unclaim')}
                        disabled={claimLoading[ticket._id]}
                        style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem', background: '#ff6b6b' }}
                      >
                        {claimLoading[ticket._id] ? 'Unclaiming...' : 'Unclaim'}
                      </ElevatedButton>
                    ) : (
                      <span style={{ color: '#a0a0a0', fontSize: '0.75rem' }}>
                        Claimed by: {ticket.assignedToName || 'Another Admin'}
                      </span>
                    )}
                  </div>
                </StatusActions>
              )}
            </TicketBody>
          </TicketDetails>
        </TicketCard>
      ))}
    </div>
  );
};

export default SupportTickets;